import axios from '@providers/axiosProvider';
import * as actions from './actions';
import {
  APIDownloadReportRequestBodyParams,
  APIDownloadReportRequestQueryParams,
  APIGetContractorListRequestParams,
  APIGetContractorListResponse,
  APIGetPropertyListRequestParams,
  APIGetPropertyListResponse,
  APIGetReportFormatListRequestParams,
  APIGetReportFormatListResponse,
  APIGetReportItemListRequestParams,
  APIGetReportItemListResponse,
} from './types';
import { APIGetDownloadResponse } from '@ducks/common/types';
import { commonActions } from '@ducks/common';

/**
 * @summary 建物リストを取得する
 * @param {APIGetPropertyListRequestParams} params リクエストパラメータ
 * @returns {Promise<void>}
 */
export const gePropertyListAsync =
  (params: APIGetPropertyListRequestParams) => async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    await axios
      .post<APIGetPropertyListResponse>(`/properties/list`, JSON.stringify({}), {
        headers: { 'Content-Type': 'application/json' },
        params: { ...params },
      })
      .then((response) => {
        const { property_list: propertyList, display_params: displayParams } = response.data.result;
        dispatch(actions.setPropertyList(propertyList));
        dispatch(actions.setPropertyListDisplayParams(displayParams));
      })
      .catch(() => {
        dispatch(actions.setReportItemList([]));
      });

    dispatch(actions.setIsLoading(false));
  };

/**
 * @summary 建物名を取得する
 * @param {number} propertyId 建物ID
 * @returns {Promise<void>}
 */
export const getReportTargetPropertyNameAsync = (propertyId: number) => async (dispatch: any) => {
  dispatch(actions.setIsLoading(true));

  await axios
    .get<{ result: { property_name: string } }>(`/properties/${propertyId}`)
    .then((response) => {
      dispatch(actions.setReportTargetName(response.data.result.property_name));
    })
    .catch(() => {
      dispatch(actions.setReportTargetName(''));
    });

  dispatch(actions.setIsLoading(false));
};

/**
 * @summary 契約先リストを取得する
 * @param {APIGetContractorListRequestParams} params リクエストパラメータ
 * @returns {Promise<void>}
 */
export const geContractorListAsync =
  (params: APIGetContractorListRequestParams) => async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    await axios
      .get<APIGetContractorListResponse>(`/properties/contracts/contractors`, {
        params: { ...params },
      })
      .then((response) => {
        const { affiliate_company_list: contractorList, display_params: displayParams } =
          response.data.result;
        dispatch(actions.setContractorList(contractorList));
        dispatch(actions.setContractorListDisplayParams(displayParams));
      })
      .catch(() => {
        dispatch(actions.setReportItemList([]));
      });

    dispatch(actions.setIsLoading(false));
  };

/**
 * @summary 契約先名を取得する
 * @param {number} contractorId 契約先ID
 * @returns {Promise<void>}
 */
export const getReportTargetContractorNameAsync =
  (contractorId: number) => async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    await axios
      .get<{ result: { company_name: string } }>(`/affiliates/companies/${contractorId}`)
      .then((response) => {
        dispatch(actions.setReportTargetName(response.data.result.company_name));
      })
      .catch(() => {
        dispatch(actions.setReportTargetName(''));
      });

    dispatch(actions.setIsLoading(false));
  };

/**
 * @summary 月次報告書の出力対象となる建物内の定期業務、工事、案件を取得する
 * @param {number} propertyId 建物ID
 * @param {number} year 年
 * @param {number} month 月
 * @param {APIGetReportItemListRequestParams} params 検索パラメータ
 * @returns {Promise<void>}
 */
export const getReportItemListByPropertyAsync =
  (propertyId: number, year: number, month: number, params: APIGetReportItemListRequestParams) =>
  async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    await axios
      .get<APIGetReportItemListResponse>(
        `/properties/${propertyId}/reports/year/${year}/month/${month}`,
        { params: params },
      )
      .then((response) => {
        const { report_items: reportItems, display_params: displayParams } = response.data.result;
        dispatch(actions.setReportItemList(reportItems));
        dispatch(actions.setReportItemDisplayParams(displayParams));
      })
      .catch(() => {
        dispatch(actions.setReportItemList([]));
      });

    dispatch(actions.setIsLoading(false));
  };

/**
 * @summary 月次報告書の出力対象となる建物内の定期業務、工事、案件を全件取得する
 * @param {number} propertyId 建物ID
 * @param {number} year 年
 * @param {number} month 月
 * @param {APIGetReportItemListRequestParams} params 検索パラメータ
 * @returns {Promise<void>}
 */
export const getReportItemListAllByPropertyAsync =
  (propertyId: number, year: number, month: number, params: APIGetReportItemListRequestParams) =>
  async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    await axios
      .get<APIGetReportItemListResponse>(
        `/properties/${propertyId}/reports/year/${year}/month/${month}/all`,
        { params: params },
      )
      .then((response) => {
        const { report_items: reportItems } = response.data.result;
        dispatch(actions.setReportItemListAll(reportItems));
      })
      .catch(() => {
        dispatch(actions.setReportItemListAll([]));
      });

    dispatch(actions.setIsLoading(false));
  };

/**
 * @summary  月次報告書の出力対象となる契約に含まれる建物内の定期業務、工事、案件を取得する
 * @param {number} contractorId 契約先ID
 * @param {number} year 年
 * @param {number} month 月
 * @param {APIGetReportItemListRequestParams} params 検索パラメータ
 * @returns {Promise<void>}
 */
export const getReportItemListByContractorAsync =
  (contractorId: number, year: number, month: number, params: APIGetReportItemListRequestParams) =>
  async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    await axios
      .get<APIGetReportItemListResponse>(
        `/affiliates/companies/${contractorId}/reports/year/${year}/month/${month}`,
        { params: params },
      )
      .then((response) => {
        const { report_items: reportItems, display_params: displayParams } = response.data.result;
        dispatch(actions.setReportItemList(reportItems));
        dispatch(actions.setReportItemDisplayParams(displayParams));
      })
      .catch(() => {
        dispatch(actions.setReportItemList([]));
      });

    dispatch(actions.setIsLoading(false));
  };

/**
 * @summary 月次報告書の出力対象となる契約に含まれる建物内の定期業務、工事、案件を全件取得する
 * @param {number} contractorId 契約先ID
 * @param {number} year 年
 * @param {number} month 月
 * @param {APIGetReportItemListRequestParams} params 検索パラメータ
 * @returns {Promise<void>}
 */
export const getReportItemListAllByContractorAsync =
  (contractorId: number, year: number, month: number, params: APIGetReportItemListRequestParams) =>
  async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    await axios
      .get<APIGetReportItemListResponse>(
        `/affiliates/companies/${contractorId}/reports/year/${year}/month/${month}/all`,
        { params: params },
      )
      .then((response) => {
        const { report_items: reportItems } = response.data.result;
        dispatch(actions.setReportItemListAll(reportItems));
      })
      .catch(() => {
        dispatch(actions.setReportItemListAll([]));
      });

    dispatch(actions.setIsLoading(false));
  };

/**
 * @summary 月報フォーマットリストを取得する
 * @param {APIGetReportFormatListRequestParams} params 検索パラメータ
 * @returns {Promise<void>}
 */
export const getReportFormatListAsync =
  (params: APIGetReportFormatListRequestParams) => async (dispatch: any) => {
    dispatch(actions.setIsReportFormatListLoading(true));

    await axios
      .get<APIGetReportFormatListResponse>(`/reports/monthly/formats`, { params: params })
      .then((response) => {
        const { report_formats: reportItems, display_params: displayParams } = response.data.result;
        dispatch(actions.setReportFormatList(reportItems));
        dispatch(actions.setReportFormatDisplayParams(displayParams));
      })
      .catch(() => {
        dispatch(actions.setReportFormatList([]));
      });

    dispatch(actions.setIsReportFormatListLoading(false));
  };

/**
 * @summary 月報フォーマット(ダウンロード用)を取得する
 * @param {number} formatId フォーマットID
 * @returns {Promise<APIGetDownloadResponse | null>}
 */
export const downloadReportFormatAsync = (formatId: number) => async () => {
  return await axios
    .get<APIGetDownloadResponse>(`/reports/monthly/formats/${formatId}`)
    .then((response) => {
      return response.data.result;
    })
    .catch(() => {
      return null;
    });
};

/**
 * @summary 月報フォーマットを登録する
 * @param {FormData} formData 登録ファイル
 * @returns {Promise<boolean>}
 */
export const uploadReportFormatAsync = (formData: FormData) => async (dispatch: any) => {
  dispatch(actions.setIsSubmitting(true));

  return await axios
    .post(`/reports/monthly/formats`, formData, {
      headers: { 'content-type': 'multipart/form-data' },
    })
    .then(() => {
      dispatch(actions.refresh());
      return true;
    })
    .catch(() => {
      return false;
    })
    .finally(() => {
      dispatch(actions.setIsSubmitting(false));
    });
};

/**
 * @summary 月報フォーマットを削除する
 * @param {number} formatId フォーマットID
 * @returns {Promise<boolean>}
 */
export const deleteReportFormatAsync = (formatId: number) => async (dispatch: any) => {
  dispatch(actions.setIsSubmitting(true));

  return await axios
    .delete(`/reports/monthly/formats/${formatId}`)
    .then(() => {
      dispatch(actions.refresh());
      return true;
    })
    .catch(() => {
      return false;
    })
    .finally(() => {
      dispatch(actions.setIsSubmitting(false));
    });
};

/**
 * @summary 建物指定で月報を出力する
 * @param {number} propertyId 建物ID
 * @param {number} year 年
 * @param {number} month 月
 * @param {APIGetReportItemListRequestParams} queryParams クエリパラメータ
 * @param {APIDownloadReportRequestBodyParams} bodyParams ボディパラメータ
 * @returns {Promise<void>}
 */
export const exportReportByPropertyAsync =
  (
    propertyId: number,
    year: number,
    month: number,
    queryParams: APIDownloadReportRequestQueryParams,
    bodyParams: APIDownloadReportRequestBodyParams,
  ) =>
  async (dispatch: any) => {
    dispatch(commonActions.setIsProcessing(true));
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .post<APIGetDownloadResponse>(
        `/properties/${propertyId}/reports/year/${year}/month/${month}/download`,
        bodyParams,
        { params: queryParams },
      )
      .then((response) => {
        return response.data.result;
      })
      .catch(() => {
        return null;
      })
      .finally(() => {
        dispatch(commonActions.setIsProcessing(false));
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 契約先指定で月報を出力する
 * @param {number} contractorId 契約先ID
 * @param {number} year 年
 * @param {number} month 月
 * @param {APIGetReportItemListRequestParams} queryParams クエリパラメータ
 * @param {APIDownloadReportRequestBodyParams} bodyParams ボディパラメータ
 * @returns {Promise<void>}
 */
export const exportReportByContractorAsync =
  (
    contractorId: number,
    year: number,
    month: number,
    queryParams: APIDownloadReportRequestQueryParams,
    bodyParams: APIDownloadReportRequestBodyParams,
  ) =>
  async (dispatch: any) => {
    dispatch(commonActions.setIsProcessing(true));
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .post<APIGetDownloadResponse>(
        `/affiliates/companies/${contractorId}/reports/year/${year}/month/${month}/download`,
        bodyParams,
        { params: queryParams },
      )
      .then((response) => {
        return response.data.result;
      })
      .catch(() => {
        return null;
      })
      .finally(() => {
        dispatch(commonActions.setIsProcessing(false));
        dispatch(actions.setIsSubmitting(false));
      });
  };
